import React from 'react';
import type { HHPlayerView } from '../../../games/henhur/henhur';
import type { TokenType } from './types/game.types';
import GamePhaseIndicator from './components/GamePhaseIndicator';
import RaceTurnControls from './components/RaceTurnControls';
import AuctionTurnControls from './components/AuctionTurnControls';
import EnhancedPlayerMat from './components/EnhancedPlayerMat';
import RaceTrack from './components/RaceTrack';
import PlayerStatusDrawer from './components/PlayerStatusDrawer';
import LeftInfoDrawer from './components/LeftInfoDrawer';
import CardHand from './components/CardHand';

interface HenHurGameEnhancedProps {
  variant?: 'standard' | 'debug';
  gameState: HHPlayerView;
  onSelectRaceCard: (cardId: string, willBurn: boolean, tokensToUse: TokenType[]) => void;
  onSelectAuctionCard: (cardId: string, willBurn: boolean, tokensToUse: TokenType[]) => void;
  onDraftCard: (cardId: string) => void;
  isLeader?: boolean;
}

const HenHurGameEnhanced: React.FC<HenHurGameEnhancedProps> = ({
  variant = 'standard',
  gameState,
  onSelectRaceCard,
  onSelectAuctionCard,
  onDraftCard,
  isLeader,
}) => {
  if (!gameState?.myState) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-900">
        <div className="text-white text-2xl font-bold mb-4">Loading HenHur...</div>
        <div className="text-gray-400">Waiting for game state...</div>
      </div>
    );
  }

  const {
    phase,
    turnNumber,
    roundNumber,
    turnType,
    track,
    myState,
    otherPlayers,
    auctionPool,
    currentDrafter,
    readyPlayers,
    winner,
  } = gameState;

  const isRaceTurn = turnType === 'race';
  const isAuctionTurn = turnType === 'auction';
  const burnSlotsAvailable = myState.burnSlots.filter(s => s.card === null).length;

  if (phase === 'game_over' && winner) {
    const winnerPlayer = winner === myState.playerId
      ? myState
      : otherPlayers.find(p => p.playerId === winner);

    return (
      <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-900">
        <div className="text-center space-y-6">
          <div className="text-6xl">🏆</div>
          <div className="text-4xl text-yellow-400 font-bold">
            {winner === myState.playerId ? 'You Win!' : `${winnerPlayer?.playerName} Wins!`}
          </div>
          <div className="text-gray-400 text-xl">
            Completed {track.lapsToWin} laps!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gray-900 py-8 pb-40 relative">
      <div className="w-full max-w-6xl mx-auto px-4 space-y-6">
        <GamePhaseIndicator
          roundNumber={roundNumber}
          turnNumber={turnNumber}
          turnType={turnType}
          phase={phase}
          readyCount={readyPlayers.length}
          totalPlayers={otherPlayers.length + 1}
        />

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <RaceTrack
            myPosition={myState.position}
            myName={myState.playerName}
            otherPlayers={otherPlayers}
            trackConfig={track}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            {isRaceTurn && (phase === 'race_selection' || myState.isReady) && (
              <RaceTurnControls
                hand={myState.deck.hand}
                tokens={myState.tokens}
                burnSlotsAvailable={burnSlotsAvailable}
                onSelectCard={onSelectRaceCard}
                isReady={myState.isReady}
              />
            )}

            {isAuctionTurn && (
              <AuctionTurnControls
                phase={
                  phase === 'auction_selection' ? 'selection' :
                  phase === 'auction_drafting' ? 'drafting' :
                  'selection'
                }
                hand={myState.deck.hand}
                tokens={myState.tokens}
                auctionPool={auctionPool || []}
                currentDrafter={currentDrafter}
                myPlayerId={myState.playerId}
                onSelectBidCard={onSelectAuctionCard}
                onDraftCard={onDraftCard}
                isReady={myState.isReady}
              />
            )}
          </div>

          <div>
            <EnhancedPlayerMat
              deckCount={myState.deck.deck.length}
              handCount={myState.deck.hand.length}
              discardCount={myState.deck.discard.length}
              burnSlots={myState.burnSlots}
              tokens={myState.tokens}
              maxTokens={myState.maxTokens}
              currentLap={myState.position.lap}
            />

            <div className="mt-4 bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-3">Other Players</h4>
              <div className="space-y-2">
                {otherPlayers.map(player => (
                  <div
                    key={player.playerId}
                    className={`bg-gray-900 rounded-lg p-3 border ${
                      readyPlayers.includes(player.playerId) ? 'border-green-600' : 'border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-semibold">
                          {player.playerName}
                          {readyPlayers.includes(player.playerId) && (
                            <span className="ml-2 text-green-400">✓</span>
                          )}
                        </div>
                        <div className="text-gray-400 text-xs">
                          Lap {player.position.lap} • Space {player.position.space}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <div>Hand: {player.handCount}</div>
                        <div>Deck: {player.deckCount}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div className="text-gray-400">Cards Played</div>
              <div className="text-white font-bold">{myState.cardsPlayed}</div>
            </div>
            <div>
              <div className="text-gray-400">Cards Burned</div>
              <div className="text-orange-400 font-bold">{myState.cardsBurned}</div>
            </div>
            <div>
              <div className="text-gray-400">Distance Moved</div>
              <div className="text-blue-400 font-bold">{myState.distanceMoved}</div>
            </div>
          </div>
        </div>
      </div>

      <PlayerStatusDrawer players={otherPlayers} readyPlayers={readyPlayers} />
      <LeftInfoDrawer variant={variant} />

      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 border-t-2 border-gray-700 backdrop-blur-sm z-40 overflow-x-auto">
        <div className="max-w-7xl mx-auto">
          <CardHand
            cards={myState.deck.hand}
            selectedCardId={null}
            disabled={false}
          />
        </div>
      </div>
    </div>
  );
};

export default HenHurGameEnhanced;
